import { useParams } from 'react-router'
import { useAppDispatch } from 'store/hooks'
import { actions as dashboardActions } from 'store/dashboard'
import useInterval from 'use-interval'
import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { RootState } from 'store'

import MultisectorTableSection from './sections/MultisectorTableSection'
import SectorSection from './sections/SectorSection'
import InformationSection from './sections/informationSection/InformationSection'
import FiltersSection from './sections/FiltersSection'
import SpotlightSection from './sections/spotlightSection/SpotlightSection'
import BenchmarkSection from 'containers/dashboard/multisectorReport/sections/benchmarkSection/BenchmarkSection'
import { groupApplicantsByApplicantCleaned } from 'containers/dashboard/multisectorReport/calculations/calculations'

import Spinner from 'components/spinner/Spinner'
import { Applicant, GroupedApplicant, MultisectorData } from 'store/dashboard/types'
import { Avatar, Box, Button, Collapse, TextField } from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import RemoveIcon from '@mui/icons-material/Remove'
import styles from './Multisector.module.scss'
import PurePlaySection from 'containers/dashboard/multisectorReport/sections/chartsSection/purePlaySection/PurePlaySection'
import IpWeigthComponent from 'containers/dashboard/multisectorReport/sections/chartsSection/IpWeightComponent'
import theme from 'containers/app/theme'
import Logs from 'containers/dashboard/multisectorReport/components/Logs'
import Descriptions from 'containers/dashboard/multisectorReport/components/Descriptions'
import { useAdminQuery } from 'containers/dashboard/multisectorReport/hooks/useAdminQuery'
import { getMultisectorCompetitors } from 'containers/dashboard/multisectorReport/calculations/competitorCalculations'

type ParamsType = {
  id: string
}

export default function MultisectorReport() {
  const isAdminView = useAdminQuery().isAdminView
  const dispatch = useAppDispatch()
  const status = useSelector((state: RootState) => state.dashboard.multisectorStatus)
  const logs = useSelector((state: RootState) => state.dashboard.multisectorLogs)
  const data: MultisectorData | null = useSelector((state: RootState) => state.dashboard.multisectorData)
  const [filteredApplicants, setFilteredApplicants] = useState<Applicant[] | null>()
  const multisectorLoading = useSelector((state: RootState) => state.dashboard.loading.multisectorData)
  const thirdPartyDataList = useSelector((state: RootState) => state.dashboard.multisectorThirdPartyDataList)
  const errorDashboard = useSelector((state: RootState) => state.dashboard.error)
  const benchmarkInfo = useSelector((state: RootState) => state.reportingBenchmark.benchmarkInfo)
  const [initializedFromBenchmark, setInitializedFromBenchmark] = useState(false)
  const [delay, setDelay] = useState<number>(6000)
  const [benchmarkApplicants, setBenchmarkApplicants] = useState<Applicant[]>([])
  const [showMultisectorTable, setShowMultisectorTable] = useState(false)
  const [groupedApplicants, setGroupedApplicants] = useState<GroupedApplicant[]>([])
  const [subgroupsSelected, setSubgroupsSelected] = useState<string[]>([])
  // subgroupsSorted contains the same subgroups that appear in info.subgroups, but this way we get them in order of appearance.
  const [subgroupsSorted, setSubgroupsSorted] = useState<string[]>([])
  const [topCompaniesNumber, setTopCompaniesNumber] = useState(10)
  const [adminShowZeroSectorVals, setAdminShowZeroSectorVals] = useState<boolean>(false)
  const applicantsFirstPerSubgroup = groupedApplicants.map(groupedApp => groupedApp.applicantBySubgroup[0])
  const [ipWeight, setIpWeight] = useState<number>()
  const competitors = getMultisectorCompetitors(applicantsFirstPerSubgroup, topCompaniesNumber, subgroupsSelected, ipWeight)

  const params = useParams<ParamsType>()
  const id = params?.id || ''

  const stopPolling = Boolean(
    errorDashboard.multisectorStatus ||
    (status &&
      [
        'regenerating',
        'whales_error',
        'multisector_error',
        'comparison_error',
        'multisector_finished',
        'comparison_finished',
      ].includes(status)),
  )

  useEffect(() => {
    if (data?.multisector) {
      const subgroups = Array.from(new Set(data.multisector.slice(0, 1000).map(item => item.subgroup)))
      setSubgroupsSorted(subgroups)
      setSubgroupsSelected(subgroups)
    }
  }, [data])

  useEffect(() => {
    if (!benchmarkInfo?.params?.companyNames) return
    if (!applicantsFirstPerSubgroup.length) return
    if (initializedFromBenchmark) return

    const applicantsFromBenchmark = benchmarkInfo.params.companyNames
      .map(name => applicantsFirstPerSubgroup.find(a => a.applicant_original_name === name))
      .filter(Boolean) as Applicant[]

    setBenchmarkApplicants(applicantsFromBenchmark)
    setInitializedFromBenchmark(true)
  }, [benchmarkInfo, applicantsFirstPerSubgroup, initializedFromBenchmark])

  useEffect(() => {
    if (ipWeight !== undefined) return
    if (applicantsFirstPerSubgroup.length > 0) {
      const targetCompany = applicantsFirstPerSubgroup[0]
      const defaultIpWeight = targetCompany.whales_patents_in_categories < 10 ? targetCompany.whales_patents_in_categories : 10
      setIpWeight(defaultIpWeight)
    }
  }, [ipWeight, applicantsFirstPerSubgroup])

  const addTopCompanies = () => {
    const existingApplicants = new Set(benchmarkApplicants.map(app => app.applicant_cleaned))
    const competitorsNotInList = competitors.filter(competitor => !existingApplicants.has(competitor.applicant_cleaned))
    setBenchmarkApplicants([...benchmarkApplicants, ...competitorsNotInList])
  }

  const regenerateMultisector = () => {
    dispatch(dashboardActions.regenerateMultisector(id))
  }

  useEffect(() => {
    dispatch(dashboardActions.getMultisectorStatus(id))
  }, [dispatch, id])

  useEffect(() => {
    dispatch(dashboardActions.getMultisectorStatus(id))
    if (data) {
      dispatch(dashboardActions.fetchMultisectorThirdPartyData(id))
    }
  }, [dispatch, data, id])

  useEffect(() => {
    if (data && data.multisector) {
      const grouped = groupApplicantsByApplicantCleaned(data.multisector, subgroupsSorted)
      setGroupedApplicants(grouped)
    }
  }, [data, subgroupsSorted])

  useInterval(
    () => {
      dispatch(dashboardActions.getMultisectorStatus(id))
    },
    stopPolling ? null : delay,
  )

  useEffect(() => {
    dispatch(dashboardActions.getMultisectorStatus(id))
  }, [dispatch, status, id])

  useEffect(() => {
    if (status === 'comparison_processing') setDelay(3000)
  }, [status])

  useEffect(() => {
    if (!data && (status === 'multisector_finished' || status === 'comparison_finished')) {
      dispatch(dashboardActions.fetchMultisectorData(id))
    }
  }, [dispatch, id, data, status])

  const renderRegenerateButton = () => {
    if (!isAdminView) return null
    if (status === 'waiting_for_searches') return null

    return (
      <Box sx={{ mb: 2 }}>
        <Button variant="contained" onClick={regenerateMultisector} sx={{ width: 500 }}>
          Regenerate multisector (admin)
        </Button>
      </Box>
    )
  }

  return (
    <Box display="flex" flexDirection="column" sx={{ py: 3, px: 6, backgroundColor: theme.palette.reportBg.main }}>
      {Boolean((multisectorLoading || !stopPolling) && !errorDashboard.multisectorData && !errorDashboard.multisectorStatus) && (
        <div className={styles.loading}>
          <Spinner />
        </div>
      )}
      {status && status !== 'multisector_finished' && <div>Status: {status}</div>}

      {!multisectorLoading && !data && (
        <>
          {renderRegenerateButton()}
          <Logs logs={logs} />
        </>
      )}

      <Box sx={{ color: 'red', mt: 2 }}>
        {errorDashboard.multisectorStatus && <div>Status error: {errorDashboard.multisectorStatus}.</div>}
        {errorDashboard.regenerateMultisector && (
          <div>Regenerate multisector error: {errorDashboard.regenerateMultisector}. </div>
        )}
        {errorDashboard.multisectorData && <div>{errorDashboard.multisectorData}</div>}
      </Box>
      {data && data.multisector?.length > 0 && (
        <>
          <InformationSection
            id={id}
            data={data}
            thirdPartyDataList={thirdPartyDataList}
            subgroups={subgroupsSorted}
            onSubgroupsSelectedChange={subgroupsSelected => setSubgroupsSelected(subgroupsSelected)}
            regenerateMultisector={regenerateMultisector}
            target={data.multisector[0]}
          />
          {groupedApplicants && groupedApplicants.length > 0 && (
            <div>
              {isAdminView && (
                <TextField
                  id="whales_patents_in_categories"
                  type="number"
                  name="whales_patents_in_categories"
                  label="Ip weight (admin)"
                  value={ipWeight}
                  sx={{ my: 1, mb: 3, input: { p: 1.3, width: 120 } }}
                  onChange={event => {
                    setIpWeight(Number(event?.target.value))
                  }}
                />
              )}
              <PurePlaySection
                competitors={competitors}
                applicantsFirstPerSubgroup={applicantsFirstPerSubgroup}
                // setChartCompetitors={setChartCompetitors}
                topCompaniesNumber={topCompaniesNumber}
                setTopCompaniesNumber={setTopCompaniesNumber}
                selectedSubgroups={subgroupsSelected}
              />
              {isAdminView && <IpWeigthComponent groupedApplicants={applicantsFirstPerSubgroup} />}
            </div>
          )}

          {groupedApplicants && (
            <>
              <SpotlightSection
                groupedApplicants={groupedApplicants}
                subgroups={subgroupsSelected}
                thirdPartyDataList={thirdPartyDataList}
              />
              <BenchmarkSection
                target={data.multisector[0]}
                subgroups={subgroupsSelected}
                benchmarkApplicants={benchmarkApplicants}
                setBenchmarkApplicants={setBenchmarkApplicants}
                topCompaniesNumber={topCompaniesNumber}
                addTopCompanies={addTopCompanies}
              />

              <FiltersSection data={data} onFilterApplied={filteredApplicants => setFilteredApplicants(filteredApplicants)} />

              {isAdminView && (
                <div>
                  <input
                    type="checkbox"
                    checked={adminShowZeroSectorVals}
                    onChange={e => {
                      setAdminShowZeroSectorVals(e.target.checked)
                    }}
                  />{' '}
                  Show zero sector values in purePlayRatio (admin)
                </div>
              )}
              {filteredApplicants &&
                subgroupsSelected.map((subgroup, index) => (
                  <SectorSection
                    key={subgroup}
                    index={index + 1}
                    subgroupCode={subgroup}
                    multisectorInfo={data.info}
                    multisectorApplicants={filteredApplicants}
                    thirdPartyDataList={thirdPartyDataList}
                    benchmarkApplicants={benchmarkApplicants}
                    setBenchmarkApplicants={setBenchmarkApplicants}
                    showZeroSectorVals={adminShowZeroSectorVals}
                  />
                ))}

              <Avatar
                sx={{ mt: 3, mb: 2, bgcolor: 'primary.main' }}
                onClick={() => setShowMultisectorTable(!showMultisectorTable)}>
                {showMultisectorTable ? <RemoveIcon sx={{ cursor: 'pointer' }} /> : <AddIcon sx={{ cursor: 'pointer' }} />}
              </Avatar>
              <Collapse in={showMultisectorTable}>
                <Logs logs={logs} />
                <Descriptions />
                <MultisectorTableSection data={data} thirdPartyDataList={thirdPartyDataList} status={status!} />
              </Collapse>
            </>
          )}
        </>
      )}
    </Box>
  )
}
